// Copyright (c) Ping. All rights reserved.
//
// CANONICAL recovery-backup bundle format — the single source of truth shared
// by web (apps/web) and desktop (apps/desktop). Previously each app defined its
// own bundle shape + base64url helpers, and desktop even uploaded the legacy
// raw SDK identity (no auth-identity, no conversation snapshots), so a desktop
// backup couldn't restore on web and vice-versa. This module removes that drift:
// both platforms build/parse the IDENTICAL `RecoveryBundleV2` and share the
// conversation-snapshot collect/import logic, so a backup written on one
// restores on the other.
//
// The blob is encrypted by the shared `encryptRecoveryBlob` (AES-GCM under the
// user passphrase) — see `network/recovery`. This module owns only the
// PLAINTEXT shape + the SDK-snapshot orchestration; the per-app hosts plug in
// their own identity export/save (Keychain on desktop, IndexedDB on web).

import { logger } from '../log/log';
import { useSessionStore } from '../messaging/sessionStore';

/** One external conversation's portable MLS state (past epoch secrets) carried
 *  in the backup so recovery restores the chat + history OFFLINE. Bytes are
 *  base64url (no pad). Forward-secrecy trade-off — see docs/architecture/recovery.md. */
export interface RecoveryBundleConversationSnapshot {
  conversation_id_b64u: string;
  group_state_bytes_b64u: string;
}

/**
 * v2 recovery payload. Wraps BOTH the SDK identity AND the auth-layer user
 * identity keypair (so restore re-pins the same `auth.accounts.user_identity_pubkey`
 * and avoids `identity_mismatch` on the next verify), plus the optional external
 * conversation snapshots (#3c). Legacy v1 backups were the raw SDK identity bytes;
 * the parser falls back to that shape.
 */
export interface RecoveryBundleV2 {
  v: 2;
  sdk_identity_b64u: string;
  auth_user_identity: {
    jwk: JsonWebKey;
    publicKeyBytes: number[];
  };
  /** Optional + additive: older restore builds ignore it; newer ones import each
   *  via `client.importStateSnapshot`. Absent when the user had no external
   *  conversations at backup time. */
  conversation_snapshots?: RecoveryBundleConversationSnapshot[];
}

export function bytesToUrlB64NoPad(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function urlB64NoPadToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = (4 - (padded.length % 4)) % 4;
  const bin = atob(padded + '='.repeat(fill));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Encode a bundle to the plaintext bytes that get passed to `encryptRecoveryBlob`. */
export function encodeRecoveryBundle(bundle: RecoveryBundleV2): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(bundle));
}

/** Parse decrypted cleartext into a v2 bundle, or `null` for legacy v1 (raw SDK
 *  identity bytes). The caller treats `null` as "wrap the raw cleartext as the
 *  SDK identity" (legacy path). */
export function parseRecoveryBundle(cleartext: Uint8Array): RecoveryBundleV2 | null {
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(cleartext));
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      (parsed as { v?: unknown }).v === 2
    ) {
      return parsed as RecoveryBundleV2;
    }
  } catch {
    // Not JSON — legacy raw SDK identity bytes.
  }
  return null;
}

/** SDK client subset the recovery snapshot path needs (both platforms' SDKs expose these). */
interface RecoverySnapshotClient {
  listConversations(): Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
  exportConversationStateSnapshot(id: Uint8Array): Promise<Uint8Array>;
  importStateSnapshot(snapshotBytes: Uint8Array): Promise<unknown>;
}

function liveClient(): RecoverySnapshotClient | null {
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') return null;
  return session.status.client as unknown as RecoverySnapshotClient;
}

/**
 * Enumerate the user's external conversations and export each one's MLS state
 * snapshot for the recovery bundle. Skips the DeviceGroup (personal state rides
 * with the identity export). Returns `[]` when there's no live client yet or no
 * external conversations. Per-conversation failures are logged + skipped so one
 * bad chat never blocks the whole backup.
 */
export async function collectConversationSnapshots(): Promise<
  RecoveryBundleConversationSnapshot[]
> {
  const client = liveClient();
  if (!client) return [];
  let metas: Array<{ id: Uint8Array; is_device_group: boolean }>;
  try {
    metas = await client.listConversations();
  } catch (err) {
    logger.warn('recovery.backup.listConversationsFailed', {
      message: (err as Error).message,
    });
    return [];
  }
  const out: RecoveryBundleConversationSnapshot[] = [];
  for (const meta of metas) {
    if (meta.is_device_group) continue;
    try {
      const snapshot = await client.exportConversationStateSnapshot(meta.id);
      out.push({
        conversation_id_b64u: bytesToUrlB64NoPad(meta.id),
        group_state_bytes_b64u: bytesToUrlB64NoPad(snapshot),
      });
    } catch (err) {
      logger.warn('recovery.backup.exportSnapshotFailed', {
        message: (err as Error).message,
      });
    }
  }
  logger.info('recovery.backup.conversationSnapshots', { count: out.length });
  return out;
}

// ---- pending-import hand-off (restore → post-bootstrap) ----
//
// The restore host decrypts the bundle and rehydrates identity, but the SDK
// client doesn't exist yet (bootstrap runs after). So the host stashes the
// snapshots here and a one-shot importer runs once the session is authenticated.
// Module-level (not persisted): a restore always flows into bootstrap in the
// same load, and these secret-bearing snapshots shouldn't linger in storage.

let pending: RecoveryBundleConversationSnapshot[] = [];

/** Called by the restore host with the decrypted bundle's snapshots (if any). */
export function stashPendingRecoverySnapshots(
  snapshots: RecoveryBundleConversationSnapshot[] | undefined,
): void {
  pending = snapshots ?? [];
}

export function hasPendingRecoverySnapshots(): boolean {
  return pending.length > 0;
}

/**
 * Import every stashed snapshot into the live SDK client, then drain the buffer.
 * Idempotent + best-effort: no-ops unless a restore stashed snapshots this load,
 * and one failed import is logged + skipped. Safe to call on every authenticated
 * transition (both apps call it post-bootstrap).
 */
export async function importPendingRecoverySnapshots(): Promise<void> {
  if (pending.length === 0) return;
  const client = liveClient();
  if (!client) return;
  const snapshots = pending;
  pending = [];
  let imported = 0;
  for (const snap of snapshots) {
    try {
      await client.importStateSnapshot(urlB64NoPadToBytes(snap.group_state_bytes_b64u));
      imported += 1;
    } catch (err) {
      logger.warn('recovery.restore.importSnapshotFailed', {
        message: (err as Error).message,
      });
    }
  }
  logger.info('recovery.restore.conversationSnapshotsImported', {
    imported,
    total: snapshots.length,
  });
}
